import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { Tooltip } from 'tdesign-react';
import HealthGauge from '@/components/HealthGauge';
import SvgIcon from '@/components/SvgIcon';
import useHealthColor from '@/hooks/useHealthColor';
import {
  fetchAllInspectionRules,
  fetchInspectionRuleExecuteResults,
} from '@/slices/kopClusterSlice';
import { isVoid } from '@/utils/common';
import { getCurrentCluster } from '@/pages/Layout/MenuLayout/CulsterAddon';
import ResultTable from './ResultTable';
import RulesTable from './RulesTable';

function HealthGaugeBlock({
  excuteResultList,
  showConfig,
  fetchInspectionRuleExecuteResults,
  fetchAllInspectionRules,
  mode = '',
  currentCluster,
  extra,
  loading,
  ...others
}) {
  const [resultVisible, setResultVisible] = useState(false);
  const [ruleVisible, setRuleVisible] = useState(false);
  const [searchParams] = useSearchParams();
  const clusterName = searchParams.get('name');
  const clusterType = currentCluster?.clusterType;

  useEffect(() => {
    if (clusterName) {
      fetchInspectionRuleExecuteResults({
        clusterName,
        mode,
      });
    }
  }, [fetchInspectionRuleExecuteResults, mode, clusterName]);

  useEffect(() => {
    if (showConfig && !isVoid(clusterType)) {
      fetchAllInspectionRules(clusterType);
    }
  }, [showConfig, clusterType, fetchAllInspectionRules]);

  const total = useMemo(() => excuteResultList.length, [excuteResultList]);
  const ok = useMemo(
    () => excuteResultList.filter((item) => item.health).length,
    [excuteResultList],
  );
  const percent = ok / total || 0;

  const [color, label] = useHealthColor(percent);

  const noExcuteResult = useMemo(() => excuteResultList.length === 0, [excuteResultList]);

  const noResultNode = useMemo(
    () =>
      noExcuteResult ? (
        <Tooltip
          visible
          theme='danger'
          content='还未获取到当前集群巡检结果，巡检会在每个小时的整点执行。'
        >
          <DangerIcon src={require('./exclamation.png')} />
        </Tooltip>
      ) : (
        extra
      ),
    [noExcuteResult, extra],
  );

  return (
    <Root {...others}>
      <HealthGauge percent={percent} />
      <Right>
        <Line1 color={color}>
          巡检状态{total === 0 ? '未知' : label}
          {showConfig && (
            <Icon
              type='setting'
              style={{ cursor: 'pointer' }}
              onClick={() => setRuleVisible(true)}
            />
          )}
          {!loading && noResultNode}
        </Line1>
        <Line2>
          {ok}/{total}
          {!noExcuteResult && (
            <DetailButton onClick={() => setResultVisible(true)}>查看详情</DetailButton>
          )}
        </Line2>
      </Right>
      <ResultTable handleClose={() => setResultVisible(false)} visible={resultVisible} />
      <RulesTable handleClose={() => setRuleVisible(false)} visible={ruleVisible} />
    </Root>
  );
}

/**
 * 健康度图表块，包含健康度状态及配置信息
 * @param {*} props
 * @param {boolean} props.showConfig 是否展示配置
 * @returns
 */
export default connect(
  (state) => ({
    excuteResultList: state.kopCluster.excuteResultList || [],
    loading: state.kopCluster.excuteResultListFetching,
    currentCluster: getCurrentCluster(state.kopCluster.allList),
  }),
  {
    fetchInspectionRuleExecuteResults,
    fetchAllInspectionRules,
  },
)(HealthGaugeBlock);

const Root = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
`;

const DetailButton = styled.div`
  font-weight: 400;
  font-size: 10px;
  line-height: 18px;
  color: #8f959f;
  padding: 1px 6px;
  background: rgba(32, 43, 64, 0.06);
  border-radius: 4px;
  cursor: pointer;
`;

const Right = styled.div`
  flex: 1;
  margin-left: 20px;
`;

const Icon = styled(SvgIcon)`
  color: #8f959f;
  margin-left: 8px;
  font-size: 16px;
  cursor: pointer;
`;

const Line1 = styled.div`
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const Line2 = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: bold;
  font-size: 20px;
  line-height: 23px;
  margin-top: 10px;
  color: #0d111a;
`;

const DangerIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-left: 2px;
  cursor: pointer;
  margin-bottom: -2px;
`;
